/**
 * Onboarding-role lane — `useClaimCoachRole` hook.
 *
 * Claims the SERVER-SIDE coach role (`users.role='trainer'`) for the signed-in
 * account via `POST /api/user/role`. This is the write the coach gate
 * (`team_create_coach_only_v1` in `handleCreateTeam`) actually consults, so the
 * coach onboarding fork MUST await it successfully BEFORE creating a team —
 * order pinned by tests/team-create-coach-only.test.js.
 *
 * Distinct from `usePersistAccountRole`, which persists the CLIENT routing
 * flag `_account_role` (read by useCoachMode / useRoleGate / today.tsx) and
 * stays best-effort inside `finish()`. That flag routes UX; it never
 * authorizes. This hook is the authorization half and is NOT best-effort:
 * it throws on any non-2xx (server refusals arrive typed — `invalid_role`,
 * `role_transition_refused`, `role_lookup_failed`, `role_write_failed` — and
 * the server message is surfaced), and the caller must treat a throw as
 * "do not create the team".
 *
 * The server accepts only the athlete-seed → trainer transition, self-only,
 * and is idempotent for an already-coach caller — so a retry after a failed
 * team create resolves cleanly.
 */

import { useCallback } from 'react';
import { useAuthFetch } from './useAuthFetch';

export interface UseClaimCoachRoleResult {
  /** Resolves when `users.role` is durably 'trainer' for the caller;
   *  rejects (typed message) on any refusal or failure. */
  claimCoachRole: () => Promise<void>;
}

interface RoleErrorBody {
  error?: string;
  code?: string;
}

export function useClaimCoachRole(): UseClaimCoachRoleResult {
  const fetchAuthed = useAuthFetch();

  const claimCoachRole = useCallback(async () => {
    const res = await fetchAuthed('/api/user/role', {
      method: 'POST',
      body: JSON.stringify({ role: 'trainer' }),
    });
    if (!res.ok) {
      let body: RoleErrorBody = {};
      try {
        body = (await res.json()) as RoleErrorBody;
      } catch {
        /* non-JSON response; fall through to the generic message. */
      }
      throw new Error(
        body?.error ?? 'Could not set up your coach account. Please try again.',
      );
    }
  }, [fetchAuthed]);

  return { claimCoachRole };
}
